/**
 * Submit for Approval Dialog
 *
 * Hard-block on submit-for-approval: the user must attach a signed contract
 * document before the request can be sent. On resubmission after rejection,
 * offer the previously uploaded document for reuse OR allow a fresh upload.
 *
 * Flow:
 *   1. On open, GET /contracts/:id/previous-submission-document
 *   2. If a previous (rejected-attempt) document exists, render two cards:
 *      - "Reuse previous" (preselected, shows file name + dates)
 *      - "Upload new"
 *      Otherwise only the upload card is shown.
 *   3. Submit button is disabled until either reuse is chosen or a new file
 *      has been uploaded successfully.
 *   4. POST /contracts/:id/submit-for-approval with { document_id }.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  Chip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { apiGet, apiPost, apiUpload } from '../../utils/apiClient';
import logger from '../../utils/logger';
import { formatDate } from '../../utils/formatters';
import { useTenant } from '../../contexts/TenantContext';

const ACCEPTED_MIME = '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const ACCEPTED_EXT_RE = /\.(pdf|doc|docx)$/i;
const MAX_BYTES = 10 * 1024 * 1024; // 10 MB — matches API multer limit

const SubmitForApprovalDialog = ({ open, contractId, onClose, onSubmitted }) => {
  const { isWorkflowDocumentRequired } = useTenant();
  const documentRequired = isWorkflowDocumentRequired('contract_submit');

  const [loadingPrevious, setLoadingPrevious] = useState(false);
  const [previous, setPrevious] = useState(null);
  // 'reuse' | 'upload' | 'skip' — 'skip' only valid when documentRequired is false
  const [mode, setMode] = useState('upload');
  const [file, setFile] = useState(null);
  const [uploadedDocId, setUploadedDocId] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const resetState = useCallback(() => {
    setPrevious(null);
    setMode('upload');
    setFile(null);
    setUploadedDocId(null);
    setUploadedFileName(null);
    setError(null);
    setUploading(false);
    setSubmitting(false);
  }, []);

  // Load previous-submission document on open
  useEffect(() => {
    if (!open || !contractId) return;
    let cancelled = false;
    resetState();
    setLoadingPrevious(true);
    apiGet(`/contracts/${contractId}/previous-submission-document`)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data || null;
        setPrevious(data);
        setMode(data ? 'reuse' : 'upload');
      })
      .catch((err) => {
        if (cancelled) return;
        // Non-fatal — treat as no previous doc
        logger.warn('Failed to load previous submission document', { message: err?.message });
        setPrevious(null);
        setMode('upload');
      })
      .finally(() => {
        if (!cancelled) setLoadingPrevious(false);
      });
    return () => { cancelled = true; };
  }, [open, contractId, resetState]);

  const handleFileChange = (event) => {
    const f = event.target.files?.[0];
    event.target.value = ''; // allow re-selecting same file later
    setError(null);
    setUploadedDocId(null);
    setUploadedFileName(null);
    if (!f) {
      setFile(null);
      return;
    }
    if (!ACCEPTED_EXT_RE.test(f.name)) {
      setFile(null);
      setError('Only PDF, DOC or DOCX files are accepted for the signed contract.');
      return;
    }
    if (f.size > MAX_BYTES) {
      setFile(null);
      setError('File is larger than the 10 MB limit.');
      return;
    }
    setFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    try {
      setUploading(true);
      setError(null);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', 'contract_signed');
      formData.append('contract_id', String(contractId));
      formData.append('notes', 'Signed contract attached on submit-for-approval');
      const res = await apiUpload('/admin/documents/upload', formData);
      const docId = res?.data?.id;
      const fname = res?.data?.file_name || file.name;
      if (!docId) {
        throw new Error('Upload succeeded but no document id was returned');
      }
      setUploadedDocId(docId);
      setUploadedFileName(fname);
    } catch (err) {
      setError('Failed to upload document: ' + (err?.message || 'Unknown error'));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    let documentId = null;
    if (mode === 'reuse') documentId = previous?.document_id || null;
    else if (mode === 'upload') documentId = uploadedDocId || null;
    // mode === 'skip' -> documentId stays null

    if (mode !== 'skip' && !documentId) return;

    try {
      setSubmitting(true);
      setError(null);
      const payload = documentId ? { document_id: documentId } : {};
      await apiPost(`/contracts/${contractId}/submit-for-approval`, payload);
      if (onSubmitted) onSubmitted();
    } catch (err) {
      const apiMsg = err?.response?.data?.error?.message || err?.message;
      setError('Failed to submit for approval: ' + (apiMsg || 'Unknown error'));
      setSubmitting(false);
    }
  };

  const canSubmit = !submitting && !uploading && (
    (mode === 'reuse' && previous?.document_id) ||
    (mode === 'upload' && uploadedDocId) ||
    (mode === 'skip' && !documentRequired)
  );

  const renderReuseCard = () => (
    <Card
      variant="outlined"
      sx={{
        borderColor: mode === 'reuse' ? 'primary.main' : 'divider',
        borderWidth: mode === 'reuse' ? 2 : 1,
      }}
    >
      <CardContent>
        <FormControlLabel
          value="reuse"
          control={<Radio />}
          label={
            <Box>
              <Typography variant="subtitle1" fontWeight="medium">
                Reuse previously uploaded document
              </Typography>
              <Typography variant="caption" color="text.secondary">
                The contract document from your last submission attempt
              </Typography>
            </Box>
          }
        />
        {previous && (
          <Box sx={{ pl: 4, mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <InsertDriveFileIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight="medium">{previous.file_name}</Typography>
            <Chip
              size="small"
              label={`Uploaded ${formatDate(previous.uploaded_at)}`}
              sx={{ ml: 1 }}
            />
            {previous.rejected_at && (
              <Chip
                size="small"
                color="warning"
                label={`Rejected ${formatDate(previous.rejected_at)}`}
              />
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderUploadCard = () => (
    <Card
      variant="outlined"
      sx={{
        borderColor: mode === 'upload' ? 'primary.main' : 'divider',
        borderWidth: mode === 'upload' ? 2 : 1,
      }}
    >
      <CardContent>
        <FormControlLabel
          value="upload"
          control={<Radio />}
          label={
            <Box>
              <Typography variant="subtitle1" fontWeight="medium">
                Upload signed contract
              </Typography>
              <Typography variant="caption" color="text.secondary">
                PDF, DOC or DOCX. Maximum 10 MB.
              </Typography>
            </Box>
          }
        />
        {mode === 'upload' && (
          <Box sx={{ pl: 4, mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {uploadedDocId ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="success" fontSize="small" />
                <Typography variant="body2" fontWeight="medium">{uploadedFileName}</Typography>
                <Chip size="small" color="success" label="Uploaded" />
                <Button
                  size="small"
                  onClick={() => { setUploadedDocId(null); setUploadedFileName(null); setFile(null); }}
                >
                  Replace
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  startIcon={<CloudUploadIcon />}
                  disabled={uploading}
                >
                  Choose file
                  <input type="file" hidden accept={ACCEPTED_MIME} onChange={handleFileChange} />
                </Button>
                {file && (
                  <Typography variant="body2" color="text.secondary">{file.name}</Typography>
                )}
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleUpload}
                  disabled={!file || uploading}
                  startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  {uploading ? 'Uploading…' : 'Upload'}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Dialog open={open} onClose={submitting || uploading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Submit Contract for Approval</DialogTitle>
      <DialogContent dividers>
        {loadingPrevious ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {documentRequired
                ? 'A signed copy of the contract is required before this can be submitted for approval.'
                : 'You can optionally attach a signed copy of the contract before submitting for approval.'}
            </Typography>
            <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {previous && renderReuseCard()}
                {renderUploadCard()}
                {!documentRequired && (
                  <Card
                    variant="outlined"
                    sx={{
                      borderColor: mode === 'skip' ? 'primary.main' : 'divider',
                      borderWidth: mode === 'skip' ? 2 : 1,
                    }}
                  >
                    <CardContent>
                      <FormControlLabel
                        value="skip"
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="subtitle1" fontWeight="medium">
                              Submit without a document
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              A signed contract is optional for your organisation. Skip the attachment and continue.
                            </Typography>
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>
                )}
              </Box>
            </RadioGroup>
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting || uploading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {submitting ? 'Submitting…' : 'Submit for Approval'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubmitForApprovalDialog;
